// Mark settled — ONE button, everywhere a settle-later trade shows.
//
// Operator, 2 Sept: Lucky's 18,00,000 USDT trade was marked settled twice
// while 84,90,697 AED was still owed and the coins undelivered. The button is
// not the settlement — money moving is — so the BFF refuses (409 legs_open)
// while a leg of the trade has neither moved nor netted. It is a per-LEG rule,
// not "the box is square": a merchant's running account is never square, and
// the old wording sent the operator looking for a zero that will never come.
// This component turns that refusal into the numbers and a deliberate second
// tap. Shared by /settle, the party page and the deal panel so the rule reads
// identically in all three.
//
// 4 Sept: this panel briefly grew a "The rest rides on their balance" button
// for a part-paid leg. It is gone, and the deletion is the point. It sent
// `force: true` — the override — under gentle words, and a forced settle
// deliberately records NO ride, so the very figure it promised was riding was
// written into free-text prose and into no audit row and onto no party page.
// It also decided by the SIZE OF THE GAP, which is the tolerance the running
// account exists not to be: paying most of a genuinely underpaid trade would
// have offered a one-tap close on it. Riding is a property of the PARTY now
// (db/0036) and the sweep does it properly, so the panel points at the party's
// page instead of doing it here badly.
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ApiError, errorWords } from '../lib/errors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, refreshMoneyViews } from '../lib/api';
import type { LegState } from '../lib/api';
import { displayAsset, fmtAmount } from '../lib/format';

/** WHAT HE PRESSES (design system §1, §4): body size, a 44 px target on a phone, centred. The sweep's one phone-target string —
 *  kit.tsx may take it over; the screens of this group carry the same line. */
const TOUCH = 'text-body py-1.5 px-3 inline-flex items-center justify-center max-lg:min-h-11';

type Legs = { take: LegState; give: LegState };
/** The one refusal this button answers with a panel: a leg is still open. */
function openLegs(e: unknown): Legs | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const body = e.body as { error?: string; legs?: Legs };
  return body.error === 'legs_open' && body.legs ? body.legs : null;
}
/** One leg in desk words: "INR received", "USDT netted", "INR 23,00,000 to receive". */
/** The ONE leg still open, if exactly one is. Two open legs get no shortcut —
 *  the operator should look at the trade rather than be handed a half-answer. */
function openSide(legs: Legs): { dir: 'in' | 'out'; asset: string; open: number } | null {
  const take = !legs.take.settled, give = !legs.give.settled;
  if (take === give) return null;                       // both open, or neither
  return take
    ? { dir: 'in',  asset: legs.take.asset, open: legs.take.open }   // they still owe us
    : { dir: 'out', asset: legs.give.asset, open: legs.give.open };  // we still owe them
}

/** The open leg when money HAS already moved on it — a part payment. It is what
 *  a running-account merchant's trade looks like from the outside, and it is
 *  ALSO what a genuinely underpaid trade looks like: the two are told apart by
 *  the party's setting, never by this number. Used only to explain the refusal,
 *  never to close anything. */
function partial(legs: Legs): { asset: string; open: number } | null {
  const side = openSide(legs);
  if (!side) return null;
  const leg = legs.take.settled ? legs.give : legs.take;
  return side.open > 0 && side.open < Number(leg.amount) ? { asset: side.asset, open: side.open } : null;
}

/** One leg in desk words: "INR received", "USDT netted", "INR on account ·
 *  15,705 rode", "INR 23,00,000 to receive".
 *
 *  `how` is printed straight, which is why the BFF spells the running-account
 *  verdict 'on account' with a space — one vocabulary, no mapping table to fall
 *  out of step with the route. WHAT RODE IS NAMED HERE and not only in the
 *  audit row: this line is what the operator actually reads on the settle
 *  screen and on the party page, and the remainder is money the desk still
 *  owes. A leg that closed for 15,705 INR less than the trade says must never
 *  read the same as one that was paid in full. */
export function legWords(l: LegState, side: 'take' | 'give'): string {
  if (l.settled) {
    // The asset is already on the line, so the figure goes on bare: "INR on
    // account · 15,705 rode", never the asset twice.
    const rode = l.rode != null && l.rode > 0 ? ` · ${fmtAmount(l.rode, l.asset)} rode` : '';
    return `${displayAsset(l.asset)} ${l.how}${rode}`;
  }
  return `${displayAsset(l.asset)} ${fmtAmount(l.open, l.asset)} ${side === 'take' ? 'to receive' : 'to pay'}`;
}

/** `className` dresses the BUTTON; `panelClassName` dresses the refusal
 *  panel that takes its place — a screen lays the panel out (full width
 *  beneath its row, first in a footer) without keying on the button's slot
 *  (Phase C review, 14 Sept 2026). */
export function SettleButton({ dealId, partySlug, onDone, className = '', panelClassName = '' }:
  { dealId: string; partySlug?: string; onDone?: () => void; className?: string; panelClassName?: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<Legs | null>(null);
  // This button also renders ON the party page. A link back to the page the
  // operator is already reading is a dead end dressed as an instruction, so
  // there the same sentence is plain text.
  const partyHref = partySlug ? `/parties/${encodeURIComponent(partySlug)}` : null;
  const onThatParty = useLocation().pathname === partyHref;
  const m = useMutation({
    // Force, or not. The note-carrying variant went with the ride button: the
    // only note this component ever sent claimed a remainder was riding on a
    // settle that recorded none, and nothing else here has anything to say that
    // the deal row and the box do not already say.
    mutationFn: (force: boolean) => api.settleDeal(dealId, { force }),
    onSuccess: () => { setOpen(null); refreshMoneyViews(qc); onDone?.(); },
    onError: (e) => {
      const legs = openLegs(e);
      if (legs) { setOpen(legs); return; }
      // ANY OTHER REFUSAL TAKES THE PANEL DOWN. The forced second tap used to
      // fail behind this panel, which then went on naming legs the refusal had
      // nothing to do with — a trade the sweep had closed in the meantime still
      // read "not settled yet". Drop the panel so the sentence below is the only
      // thing left on screen.
      setOpen(null);
      // The sweep closed it while the panel was up, so the books have already
      // moved on: pull the lists in behind the message rather than leaving a
      // row the operator can tap again.
      if (e instanceof ApiError && e.code === 'not_awaiting') refreshMoneyViews(qc);
    },
  });
  const otherError = m.isError && !openLegs(m.error) ? errorWords(m.error) : null;

  if (open) {
    // 21 Oct 2026 audit ("too much descriptive everything you are building"): the
    // refusal was ~120 words. It keeps the reason (the headline names the open
    // legs), the one action that closes it, "Keep it open" and "Mark settled
    // anyway" (whose confirm states the consequence). The "Record the money with
    // Receive / Pay; the trade closes itself once…" paragraph is gone — Settle
    // later's About this screen says the same in its first paragraph.
    //
    // Sweep, 21 Oct 2026 (design system): the headline is a state — body size, ten
    // words at most with the legs ("Not settled — AED 229,500 to receive · USDT
    // 62,449 to pay."), the buttons are what he presses (body size, 44 px on a phone).
    return (
      <div className={`rounded-lg bg-warn-bg p-2.5 text-meta space-y-2 ${panelClassName}`}>
        <p className="text-body font-medium text-warn">Not settled — {legWords(open.take, 'take')} · {legWords(open.give, 'give')}.</p>
        <div className="flex flex-wrap gap-2">
          {/* THE PANEL NAMES THE EXACT SHORTFALL — SO IT SHOULD BE ONE TAP.
              4 Sept: Loki's trade owed 20,99,405 INR, 20,83,700 was paid, and
              this panel said "INR 15,705 to pay" while offering no way to pay
              it. The only Pay link on the screen prefills the party's WHOLE box
              (91,767 there), so closing a trade meant reading a number off one
              panel and typing it into another — which is how the 15,705 was
              left behind in the first place. */}
          {partySlug && openSide(open) && (
            <Link
              className={`btn-secondary ${TOUCH}`}
              to={`/record?party=${encodeURIComponent(partySlug)}&dir=${openSide(open)!.dir}` +
                  `&asset=${openSide(open)!.asset}&amount=${openSide(open)!.open}`}
            >
              {openSide(open)!.dir === 'out' ? 'Pay' : 'Receive'} {fmtAmount(openSide(open)!.open, openSide(open)!.asset)}{' '}
              {displayAsset(openSide(open)!.asset)}
            </Link>
          )}
          <button type="button" className={`btn-secondary ${TOUCH}`} onClick={() => setOpen(null)}>Keep it open</button>
          {/* A NATIVE CONFIRM IN THE PANEL'S OWN WORDS (sweep item 33, 14 Sept
              2026): this is a forced write that records no ride, and it was
              one tap. The confirm names the deal and what is still open, the
              same pattern the party page uses for Promote and Running account. */}
          <button type="button" className={`btn-secondary ${TOUCH} text-neg`} disabled={m.isPending} onClick={() => {
            const still = ([['take', open.take], ['give', open.give]] as const)
              .filter(([, l]) => !l.settled)
              .map(([side, l]) => `${displayAsset(l.asset)} ${fmtAmount(l.open, l.asset)} still to ${side === 'take' ? 'receive' : 'pay'}`)
              .join(' and ');
            if (confirm(`Mark ${dealId} settled with ${still}? This records no ride — the figure is written off, not carried on their account.`)) m.mutate(true);
          }}>
            {m.isPending ? 'Marking…' : 'Mark settled anyway'}
          </button>
        </div>
        {/* PART-PAID, AND THE PANEL SAYS WHERE THAT IS DECIDED. Money has moved
            on this leg and some is still open — which is what a running-account
            merchant's trade looks like AND what a genuinely underpaid trade
            looks like. Nothing here can tell them apart, and nothing here
            should try: that is the party's setting, made once with a name and a
            date on it, not a judgement made per trade at the moment somebody is
            trying to clear a screen. So this points at the page where the
            decision belongs and states the two cases where it does not apply.

            21 Oct 2026 audit: ONE line now. The open figure is in the headline
            above; the sweep's rule ("closes trades like this one by itself and
            records what rode") and the two cases where it does not apply (a leg
            you have held, a trade you have reopened) are in Settle later's About
            this screen. The pointer to the party page — the one thing a tap can
            act on — stays, and the confirm on "Mark settled anyway" still says
            the figure is written off, not carried.

            Astra, 21 Oct: this button also stands on the party page and in a
            deal's slide-over, which have no About — so the exception that stops
            a wrong click, in its shortest form, stays HERE: a setting on the
            party never closes a leg he has held or a trade he has reopened. */}
        {/* Sweep, 21 Oct 2026: the pointer is ONE line inside the budget (twelve words) — it was thirteen — and
            the exception stays whole on its own line. */}
        {partial(open) && (
          <div className="space-y-0.5 text-meta text-tg-hint">
            <p>
              Part-paid: short, or their margin? Set Running account on{' '}
              {partyHref && !onThatParty
                ? <Link to={`${partyHref}?entity=dubaiotc`} className="btn-link text-meta">their party page</Link>
                : 'their party page'}.
            </p>
            <p>Running account never closes a held or reopened trade.</p>
          </div>
        )}
        {/* Backstop. onError already takes this panel down for anything that
            is not legs_open, so a refusal normally shows on the button — but a
            forced attempt failing in silence is the bug that started all this,
            and it must not be able to come back through a path added later. */}
        {/* words:ok — a refusal's own sentence is the reason; shown whole */}
        {otherError && <p className="text-meta text-neg">{otherError}</p>}
      </div>
    );
  }
  // 21 Oct 2026 audit: ten of these stood in a column beside eleven Receive / Pay
  // doors, all the same weight. Receive and Pay are how a trade closes; this is
  // the override, so it keeps its outline (still plainly a button) but not the
  // fill, and the muted text.
  return (
    <span className={`inline-flex flex-col items-end gap-1 ${className}`}>
      {/* The override keeps its quiet look (outlined, muted, small) and its 44 px phone target comes from an invisible hit area
          (the ::after reaches 10 px above and below the box) — a real 44 px button made it weigh as much as Receive / Pay. */}
      <button type="button" className="btn-secondary relative text-meta py-1 px-2 shrink-0 bg-transparent text-tg-hint hover:text-tg-text max-lg:after:absolute max-lg:after:-inset-y-2.5 max-lg:after:inset-x-0 max-lg:after:content-['']" disabled={m.isPending} onClick={() => m.mutate(false)}>
        {m.isPending ? 'Settling…' : 'Mark settled'}
      </button>
      {/* words:ok — a refusal's own sentence is the reason; shown whole */}
      {otherError && <span className="text-meta text-neg">{otherError}</span>}
    </span>
  );
}
